import React, {useEffect, useState} from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import Icon from 'sail/Icon';
import {Heading, Body, BodyAltMono} from 'sail/Text';
import {ButtonLink} from 'sail/Button';
import {TooltipContainer} from 'sail/Tooltip';
import {CardElement, useStripe, useElements} from '@stripe/react-stripe-js';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useApi} from 'docs/data';
import './CardDemo.scss';
import {FormattedMessageOrString} from 'docs/data/localization/intlUtils';

const messages = defineMessages({
  succeeds: {
    id: 'docsApp.integrationBuilder.demo.cardForms.succeeds',
    description: 'Label for when payment succeeds',
    defaultMessage: 'Payment succeeds',
  },
  auth: {
    id: 'docsApp.integrationBuilder.demo.cardForms.auth',
    description: 'Label for when auth is required for payment',
    defaultMessage: 'Authentication required',
  },
  declined: {
    id: 'docsApp.integrationBuilder.demo.cardForms.declined',
    description: 'Label for when payment is declined',
    defaultMessage: 'Payment is declined',
  },
  testCards: {
    id: 'docsApp.integrationBuilder.demo.cardForms.testCards',
    description: 'Explanation of test cards',
    defaultMessage:
      'These test card numbers work with any CVC, postal code and future expiry date.',
  },
  viewDetails: {
    id: 'docsApp.integrationBuilder.demo.cardForms.viewDetails',
    description: 'Link to view payment details',
    defaultMessage:
      'View payment details in the <button>Stripe Dashboard</button>',
  },
  tryAgain: {
    id: 'docsApp.integrationBuilder.demo.cardForms.tryAgain',
    description: 'Label for Try Again button',
    defaultMessage: 'Try again',
  },
  disabledDev: {
    id: 'docsApp.integrationBuilder.demo.cardForms.disabledDev',
    description:
      'Disabled message (internal, not super important to get a good translation here)',
    defaultMessage: 'Disabled in local dev',
  },
  unable: {
    id: 'docsApp.integrationBuilder.demo.cardForms.unable',
    description: 'Unable to pay error message',
    defaultMessage: 'Unable to pay',
  },
  payNow: {
    id: 'docsApp.integrationBuilder.demo.cardForms.payNow',
    description: 'Pay now button label',
    defaultMessage: 'Pay now',
  },
  makeTest: {
    id: 'docsApp.integrationBuilder.demo.cardForms.makeTest',
    description: 'Make a test payment label',
    defaultMessage: 'Make a test payment',
  },
  testFailed: {
    id: 'docsApp.integrationBuilder.demo.cardForms.testFailed',
    description: 'Test payment failed header',
    defaultMessage: 'Test payment failed',
  },
  testSuccessful: {
    id: 'docsApp.integrationBuilder.demo.cardForms.testSuccessful',
    description: 'Test payment successful label',
    defaultMessage: 'Test payment successful',
  },
});

const CARDS = [
  {label: 'Payment succeeds', number: '4242 4242 4242 4242'},
  {label: 'Authentication required', number: '4000 0025 0000 3155'},
  {label: 'Payment is declined', number: '4000 0000 0000 9995'},
];

const cardElementOptions = ({cardText, cardFont, cardInvalid}) => {
  return {
    base: {
      color: cardText,
      fontFamily: cardFont,
      fontSmoothing: 'antialiased',
      fontSize: '14px',
      '::placeholder': {
        color: cardText,
      },
    },
    invalid: {
      color: cardInvalid,
      iconColor: cardInvalid,
      fontFamily: cardFont,
    },
  };
};

const clearCardElement = (cardElement) => {
  cardElement.update({
    __privateValue: {
      cardNumber: '',
      cardExpiry: '',
      cardCvc: '',
      postalCode: '',
    },
  });
};

const Card = ({card}) => {
  const elements = useElements();
  const cardElement = elements.getElement(CardElement);

  const fillText = (valueName, valueContent, delay = 0) => {
    setTimeout(function() {
      let progress = 0;
      const timer = setInterval(function() {
        const valueContentPartial = String(valueContent).substring(
          0,
          progress++,
        );
        // It's a Stripe Element, so use the private API to update the partial value.
        const value = {};
        value[valueName] = valueContentPartial;
        cardElement.update({__privateValue: value});

        if (progress > valueContent.length) {
          clearInterval(timer);
        }
      }, 40);
    }, delay);
  };

  return (
    <Box flex={{alignItems: 'center', justifyContent: 'space-between'}}>
      <Body color="dark">
        <FormattedMessageOrString stringOrDefineMessage={card.label} />
      </Body>
      <ButtonLink
        label={
          <BodyAltMono color="inherit" style={{fontWeight: '600'}}>
            {card.number}
          </BodyAltMono>
        }
        onClick={() => {
          if (cardElement) {
            clearCardElement(cardElement);
            fillText('cardNumber', card.number);
            fillText('cardExpiry', '0824', 1000);
            fillText('cardCvc', '123', 1600);
            fillText('postalCode', '94107', 2400);
          }
        }}
      />
    </Box>
  );
};

const TestCards = () => {
  return (
    <Group direction="vertical" spacing={8}>
      {CARDS.map((card) => (
        <Card card={card} key={card.number} />
      ))}
      <Box margin={{top: 12}}>
        <Body>
          <FormattedMessage {...messages.testCards} />
        </Body>
      </Box>
    </Group>
  );
};

const Result = ({icon, header, onClick}) => {
  return (
    <Box
      flex={{
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      padding={{bottom: 24}}
      className="CardForm--result"
    >
      <Icon icon={icon} color="white" style={{marginBottom: '12px'}} />
      <Heading color="white">{header}</Heading>
      <ButtonLink
        label={<FormattedMessage {...messages.tryAgain} />}
        style={{margin: 0, marginTop: '12px'}}
        onClick={onClick}
      />
    </Box>
  );
};

export const CardForm = () => {
  const [disabled, setDisabled] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const stripe = useStripe();
  const elements = useElements();
  const {api} = useApi();

  const demoSettings = {};

  const handleFetch = async () => {
    if (elements) {
      const element = elements.getElement('card');
      if (element) {
        // Clear the input of the existing Element
        clearCardElement(element);
      }
    }
    const response = await api.createPaymentIntent({
      payment_method_type: 'card-element',
      payment_method_types: ['card'],
    });
    if (response.clientSecret) {
      setClientSecret(response.clientSecret);
      setPaymentId('');
      setError('');
      setDisabled(true);
      setProcessing(false);
    }
  };

  useEffect(() => {
    handleFetch();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  const handleSubmit = async (ev) => {
    ev.preventDefault();

    // Make the test payment
    setProcessing(true);
    const cardElement = elements.getElement(CardElement);
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });
    setProcessing(false);

    if (result.error) {
      setError(result.error);
      setPaymentId(
        result.error.payment_intent ? result.error.payment_intent.id : '',
      );
    } else if (result.paymentIntent) {
      setPaymentId(result.paymentIntent.id);
    }
  };

  const cardStyle = cardElementOptions(demoSettings);
  const emailStyle = {
    borderRadius: '4px',
    marginBottom: '8px',
    padding: '12px',
    border: '1px solid rgba(50, 50, 93, 0.1)',
    height: '0',
    fontSize: '14px',
    width: '100%',
    maxWidth: '400px',
    background: 'white',
    color: '#32325d',
    outline: 'none',
    transition:
      'height 0s, opacity 0.2s ease-in-out, transform 0.2s ease-in-out',
    opacity: 0,
    transform: 'translateY(20px) scale(0.9)',
  };

  // Demo does not work locally :(
  const errorMsg = /(dev\.stripe\.me)/.test(window.location.hostname)
    ? messages.disabledDev
    : messages.unable;
  const buttonText = clientSecret ? messages.payNow : errorMsg;
  const disableButton = disabled || !clientSecret;

  return (
    <>
      {/* Only show the CardElement if a previous payment has not been made */}
      <Box style={{display: error || paymentId ? 'none' : 'block'}}>
        <input
          type="text"
          placeholder="Enter email address"
          id="email"
          style={emailStyle}
        />
        <form
          className="CardPayment"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transition: 'all 0.2s ease-in-out',
          }}
          onSubmit={handleSubmit}
        >
          <CardElement
            options={{style: cardStyle}}
            onChange={(event) => {
              setDisabled(event.empty);
            }}
          />
          <button
            type="submit"
            style={{
              color: 'rgb(255, 255, 255)',
              background: 'rgb(84, 105, 212)',
              fontFamily: 'Arial, sans-serif',
              opacity: disableButton ? 0.5 : 1,
              width: '400px',
              margin: 0,
              height: '40px',
              borderRadius: '0 0 6px 6px',
              fontSize: '14px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            disabled={disableButton}
          >
            <span
              className={
                processing
                  ? 'CardPayment-spinner'
                  : 'CardPayment-spinner hidden'
              }
            />
            {!processing && <FormattedMessage {...buttonText} />}
          </button>
        </form>

        <Box
          className="CardForm--makeTest"
          margin={{top: 16, bottom: 24}}
          flex={{justifyContent: 'center'}}
        >
          <TooltipContainer
            allowMouseOverContents
            context={
              <ButtonLink
                label=<FormattedMessage {...messages.makeTest} />
                size="small"
              />
            }
            tooltipContents={
              <TestCards element={elements && elements.getElement('card')} />
            }
            position="below"
            contentMaxWidth={400}
          />
        </Box>
      </Box>
      {error || paymentId ? (
        <Result
          icon={error ? 'block' : 'check'}
          header={
            error ? (
              <FormattedMessage {...messages.testFailed} />
            ) : (
              <FormattedMessage {...messages.testSuccessful} />
            )
          }
          onClick={handleFetch}
        />
      ) : (
        ''
      )}
    </>
  );
};
